import React, { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import { getApiOrganizationSearch } from "../../../../services/Admin/organizations";
import { apiRefreshToken } from "../../../../services/Auth/login";
import EditOrganizationModal from "./EditOrganizationModal";
import useAuth from "../../../../hooks/useAuth";
import Tooltip from "@mui/material/Tooltip";
import LoadingProgress from "../../../../components/LoadingProgress";

import { FaUser } from "react-icons/fa";

export default function Organization() {
    const { auth, login } = useAuth()
    const [organizations, setOrganizations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editOpen, setEditOpen] = useState(false);
    const [editOrg, setEditOrg] = useState(null);
    const [isCreate, setIsCreate] = useState(false);

    const isSuperAdmin = auth ? auth?.primaryUser?.isSuperAdmin : 0
    const role = auth ? auth?.primaryUser?.role : null

    // Auth
    const organizationId = auth ? auth?.primaryUser?.organizationId : null

    const getOrganization = () => {
        setLoading(true);
        getApiOrganizationSearch()
            .then(res => {
                setOrganizations(res.data || []);
            })
            .catch(error => {
                setOrganizations([]);
                console.error(error)
            })
            .finally(() => setLoading(false));
    };

    const handleEdit = (org) => {
        setEditOrg(org);
        setIsCreate(false);
        setEditOpen(true);
    };

    const handleAdd = () => {
        setEditOrg({});
        setIsCreate(true);
        setEditOpen(true);
    };

    const handleEditClose = () => {
        setEditOpen(false);
        setEditOrg({});
        setIsCreate(false);
    };

    const refreshToken = (id) => {
        apiRefreshToken({ organizationId: id })
            .then(res => {
                changeToken(res.data?.token)
            })
            .catch(error => {
                console.error("Error refreshing token:", error);
            });
    }


    const changeToken = async (token) => {
        await login({
            primaryUser: token,
            secondaryUser: auth?.secondaryUser,
        })
    }

    useEffect(() => {
        getOrganization();
    }, [editOpen]);

    return (
        <>
            <div className="cointainer cointainer-bt">
                <h1 style={{ margin: 0 }}>Organizaciones</h1>
                {isSuperAdmin ? (
                    <Button
                        variant="outlined"
                        color="primary"
                        size="medium"
                        style={{ width: 200 }}
                        onClick={handleAdd}
                    >
                        Añadir
                    </Button>
                ) : null}
            </div>
            {/* <div style={{ margin: "auto", maxWidth: 1200 }}> */}
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 3,
                    mt: 2,
                }}
            >
                {loading ? (
                        <LoadingProgress loading={true} />
                ) : organizations.length === 0 ? (
                    <Typography>No hay organizaciones.</Typography>
                ) : (
                    organizations.map(org => (
                        <Card
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                minHeight: 120,
                                borderRadius: '24px',
                                boxShadow: 'none',
                                border: '1.5px solid #e3e6ee',
                                background: '#fff',
                                paddingLeft: 2,
                                zIndex: 1,
                                position: 'relative',
                                width: 450,
                                minWidth: 450,
                                maxWidth: 450,
                                flexDirection: 'row',
                            }}
                        >
                            <Box
                                sx={{
                                    height: 96,
                                    width: 96,
                                    minHeight: 64,
                                    minWidth: 64,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '16px',
                                    background: '#fff',
                                    border: '1.5px solid #e3e6ee',
                                    mr: 3,
                                    p: 1.5,
                                }}
                            >
                                <Box
                                    component="img"
                                    src={org.logo || '/no-image-available.jpg'}
                                    alt={org.name}
                                    sx={{
                                        maxHeight: '100%',
                                        maxWidth: '100%',
                                        objectFit: 'contain',
                                        borderRadius: '10px',
                                        background: '#fff',
                                        boxShadow: '0 1px 4px 0 rgba(0,0,0,0.03)'
                                    }}
                                />
                            </Box>
                            <CardContent sx={{ flex: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', mb: 1 }}>
                                    <Typography
                                        variant="h6"
                                        component="div"
                                        sx={{
                                            flex: 1,
                                            fontWeight: 700,
                                            fontSize: 20,
                                            color: '#223',
                                            textOverflow: 'ellipsis',
                                            overflow: 'hidden',
                                            whiteSpace: 'nowrap',
                                        }}
                                        title={org.name} // // Accesibilidad: título completo al pasar el ratón
                                    >
                                        {org.name}
                                    </Typography>

                                    {/* Right side: total users pill with breakdown on hover */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 48, justifyContent: 'flex-end', ml: 2 }}>
                                        <Tooltip
                                            title={
                                                // // Tooltip: desglose por tipo de usuario
                                                <Box sx={{ display: 'grid', gap: 0.25 }}>
                                                    <span><strong>Admins:</strong> {(org.userAdmin ?? 0).toLocaleString()}</span>
                                                    <span><strong>Gestores:</strong> {(org.userManager ?? 0).toLocaleString()}</span>
                                                    <span><strong>Trabajadores:</strong> {(org.userWorker ?? 0).toLocaleString()}</span>
                                                    <span><strong>Clientes:</strong> {(org.userClient ?? 0).toLocaleString()}</span>
                                                </Box>
                                            }
                                            arrow
                                        >
                                            <Box
                                                role="status" // // Accesibilidad: indica información de estado
                                                aria-label={`Total users ${(
                                                    (org.userAdmin ?? 0) +
                                                    (org.userManager ?? 0) +
                                                    (org.userWorker ?? 0) +
                                                    (org.userClient ?? 0)
                                                ).toLocaleString()}`}
                                                sx={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    background: '#eeeeee',
                                                    borderRadius: '999px',
                                                    px: 1.25,
                                                    py: 0.5,
                                                    gap: 0.5,
                                                    border: '1px solid #e3e6ee',
                                                }}
                                            >
                                                {/* // Icono alineado centrado sin hacks */}
                                                <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 0, marginTop: "-10px" }}>
                                                    <FaUser size={13} color="#9aa0a6" />
                                                </Box>

                                                {/* // Total de usuarios con separador de miles */}
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: '#616161',
                                                        fontWeight: 700,
                                                        fontSize: 14,
                                                        minWidth: 18,
                                                        textAlign: 'right',
                                                        letterSpacing: 0.2,
                                                    }}
                                                >
                                                    {(
                                                        (org.userAdmin ?? 0) +
                                                        (org.userManager ?? 0) +
                                                        (org.userWorker ?? 0) +
                                                        (org.userClient ?? 0)
                                                    ).toLocaleString()}
                                                </Typography>
                                            </Box>
                                        </Tooltip>
                                    </Box>
                                </Box>
                                <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        size="small"
                                        onClick={() => refreshToken(org.id)}
                                        disabled={organizationId === org.id}
                                        sx={{
                                            flex: 1,
                                            minWidth: 0,
                                            fontSize: 13,
                                            padding: '6px 0',
                                            borderRadius: '16px',
                                            background: '#f7fafd',
                                            color: '#1976d2',
                                            borderColor: '#bbdefb',
                                            boxShadow: 'none',
                                            transition: 'background 0.2s, border 0.2s',
                                            '&:hover': {
                                                background: '#e3f2fd',
                                                borderColor: '#90caf9',
                                            },
                                        }}
                                    >
                                        Seleccionar
                                    </Button>
                                    {role === "ROLE_ADMIN" ? (
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            size="small"
                                            sx={{
                                                flex: 1,
                                                minWidth: 0,
                                                fontSize: 13,
                                                padding: '6px 0',
                                                borderRadius: '16px',
                                                background: '#f7fafd',
                                                color: '#1976d2',
                                                borderColor: '#bbdefb',
                                                boxShadow: 'none',
                                                transition: 'background 0.2s, border 0.2s',
                                                '&:hover': {
                                                    background: '#e3f2fd',
                                                    borderColor: '#90caf9',
                                                },
                                            }}
                                            onClick={() => handleEdit(org)}
                                        >
                                            Gestionar
                                        </Button>
                                    ) : null}
                                </Box>
                            </CardContent>
                        </Card>
                    ))
                )}
            </Box>
            <EditOrganizationModal
                role={role}
                open={editOpen}
                onClose={handleEditClose}
                organization={editOrg}
                onUpdated={getOrganization}
                isCreate={isCreate}
                isSuperAdmin={isSuperAdmin}
            />
        </>
    );
}
